import React, { useMemo, useState } from 'react';
import { Text, TextInput, View, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import SolidButton from '@/components/General/SolidButton';
import { sortMilestoneThresholds } from '@/utils/milestones';

/**
 * Sorted tier editor for a milestone series.
 *
 * Earned tiers (awarded to any subject) are immutable: rendered disabled with
 * a lock and an explanation. The server re-enforces the same rule — an
 * unearned tier edited here may still fail if someone earns it mid-edit.
 *
 * Typing edits a per-row draft only; the committed (and re-sorted) value is
 * applied on blur/end-editing so the row doesn't jump position or drop the
 * keyboard mid-entry.
 */
const MilestoneThresholdEditor = ({ thresholds = [], earnedThresholdIDs = [], disabled = false, maxThresholds = null, onChange, error = null }) => {
    const earned = useMemo(() => new Set(earnedThresholdIDs ?? []), [earnedThresholdIDs]);
    const sorted = useMemo(() => sortMilestoneThresholds(thresholds), [thresholds]);
    const atMax = maxThresholds != null && thresholds.length >= maxThresholds;
    // Draft text per tier key while a row is being edited — committed on blur.
    const [drafts, setDrafts] = useState({});

    const commitTier = (tier) => {
        const key = tier.id ?? `new-${thresholds.indexOf(tier)}`;
        const draft = drafts[key];
        if (draft === undefined) return;
        const digits = String(draft).replace(/[^0-9]/g, '');
        const index = thresholds.indexOf(tier);
        const next = thresholds.map((t, i) => i === index ? { ...t, threshold: digits === '' ? '' : Number(digits) } : t);
        setDrafts(prev => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
        onChange(next);
    };

    const removeTier = (index) => {
        const tier = thresholds[index];
        if (tier?.id && earned.has(tier.id)) return;
        onChange(thresholds.filter((_, i) => i !== index));
    };

    const addTier = () => {
        if (disabled || atMax) return;
        const highest = sorted.length ? Number(sorted[sorted.length - 1].threshold) || 0 : 0;
        const suggestion = sorted.length ? Math.max(highest * 2, highest + 1) : 10;
        onChange([...thresholds, { id: null, threshold: suggestion }]);
    };

    return (
        <View style={styles.container}>
            <View style={styles.headerRow}>
                <Text style={styles.label}>
                    Thresholds
                    <Text style={styles.required}>*</Text>
                </Text>
                {!disabled && (
                    <SolidButton
                        testID="milestone-threshold-add"
                        size="sm"
                        variant="muted"
                        type="primary"
                        text="Add Tier"
                        iconName="add"
                        iconBefore
                        disabled={atMax}
                        onPress={addTier}
                    />
                )}
            </View>
            <Text style={styles.helpText}>
                A badge is earned each time the count reaches a tier. Earned tiers can&apos;t be changed or removed.
            </Text>

            <View style={styles.tierList}>
                {sorted.map((tier) => {
                    const index = thresholds.indexOf(tier);
                    const isEarned = tier?.id ? earned.has(tier.id) : false;
                    const locked = disabled || isEarned;
                    const key = tier.id ?? `new-${index}`;
                    const displayValue = key in drafts ? drafts[key] : (tier.threshold === '' || tier.threshold == null ? '' : String(tier.threshold));
                    return (
                        <View key={key} style={styles.tierRow} testID={`milestone-threshold-${tier.id ?? index}`}>
                            <View style={styles.tierInputWrap}>
                                <TextInput
                                    style={styles.tierInput(locked)}
                                    value={displayValue}
                                    onFocus={() => setDrafts(prev => ({ ...prev, [key]: displayValue }))}
                                    onChangeText={(text) => setDrafts(prev => ({ ...prev, [key]: text.replace(/[^0-9]/g, '') }))}
                                    onBlur={() => commitTier(tier)}
                                    onEndEditing={() => commitTier(tier)}
                                    keyboardType="number-pad"
                                    inputMode="numeric"
                                    editable={!locked}
                                    placeholder="0"
                                    placeholderTextColor="#999"
                                    accessibilityLabel={`Threshold ${index + 1}${isEarned ? ' (earned, locked)' : ''}`}
                                />
                                {/* One corner slot overlaid on the input's
                                    right edge — lock for tiers that can't be
                                    removed, clear affordance for the rest.
                                    Nothing sits beside the input. */}
                                {locked ? (
                                    <View style={styles.cornerSlot} pointerEvents="none" accessibilityLabel="Threshold locked">
                                        <Unicon name="lock-closed" size={14} colorVariant="textSecondary" />
                                    </View>
                                ) : (
                                    <Pressable
                                        testID={`milestone-threshold-remove-${tier.id ?? index}`}
                                        hitSlop={8}
                                        onPress={() => removeTier(index)}
                                        style={styles.cornerSlot}
                                        accessibilityLabel="Remove threshold"
                                        accessibilityRole="button"
                                    >
                                        <Unicon name="close" size={16} colorVariant="textSecondary" />
                                    </Pressable>
                                )}
                            </View>
                        </View>
                    );
                })}
            </View>

            {sorted.some(t => t?.id && earned.has(t.id)) && (
                <Text style={styles.lockHint}>
                    <Unicon name="lock-closed" size={11} colorVariant="textSecondary" /> Locked tiers have already been earned and are permanent.
                </Text>
            )}
            {!!error && <Text style={styles.errorText}>{error}</Text>}
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing, roundness, shadows }) => ({
    container: {
        marginVertical: spacing.sm,
        gap: spacing.xs,
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    label: {
        color: colors.textSecondary,
        fontFamily: fonts.heading,
        fontSize: 13,
    },
    required: {
        color: colors.error,
        fontSize: 14,
    },
    helpText: {
        color: colors.textTertiary,
        fontFamily: fonts.body,
        fontSize: 12,
    },
    tierList: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: spacing.sm,
        marginTop: spacing.xs,
    },
    tierRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    tierInputWrap: {
        position: 'relative',
    },
    tierInput: (locked) => ({
        width: 84,
        height: 40,
        borderRadius: roundness.sm,
        backgroundColor: colors.inputBackground,
        color: colors.text,
        fontFamily: fonts.body,
        fontSize: 14,
        textAlign: 'center',
        paddingLeft: spacing.sm,
        // Reserve room for the corner slot (lock/clear) so digits don't
        // render underneath it.
        paddingRight: 22,
        opacity: locked ? 0.6 : 1,
        _web: {
            outlineStyle: 'none',
            _focus: { boxShadow: shadows.input },
        },
    }),
    cornerSlot: {
        position: 'absolute',
        right: 4,
        top: 0,
        bottom: 0,
        justifyContent: 'center',
        zIndex: 1,
        _web: { cursor: 'pointer' },
    },
    lockHint: {
        color: colors.textTertiary,
        fontFamily: fonts.body,
        fontSize: 11,
        marginTop: spacing.xs,
    },
    errorText: {
        color: colors.error,
        fontFamily: fonts.body,
        fontSize: 12,
    },
}));

export default MilestoneThresholdEditor;
